import { CaretRightOutlined } from "@ant-design/icons"
import DOMPurify from "dompurify"
import { useEffect, useState } from "react"
import config from "react-global-configuration"
import { useDispatch, useSelector } from "react-redux"

import { Button } from "components/Button"
import { Buttons } from "components/Card/Card"
import { FormPage } from "components/Form/Form"
import { Code, CodeInputProps } from "components/Input/Code/Index"
import Message from "components/Message/Message"
import { GenericModal } from "components/Modal/Modal"
import PreviousButton from "components/Previous/PreviousButton"
import { TwoFaModal } from "containers/Login/Login"
import { logout } from "containers/Login/services/actions"
import { cdn } from "core/cdn"
import { RESET_PASSWORD_PAGE } from "core/constants"
import { Keyboard } from "hocs/withKeyboard"
import { request } from "lib/request"
import { useLanguage } from "locales"
import { Message as MessageInterface } from "types/redux"
import { ClientStore } from "types/store"

import styles from "./AccountDeletion.module.scss"

type Step = "menu" | "warning" | "password" | "code" | "done"
type Transport = "sms" | "voice" | "email"

interface DeletionRequestResponse {
  status: string
  confirmed?: boolean
  customer?: { type: Transport; update_verify_id: string }
}

interface DeletionResendResponse {
  status: string
  type: Transport
  update_verify_id: string
}

const CODE_LENGTH = 6

const cdnBackground = (path: string) => ({ backgroundImage: `url(${cdn(path)})` })

// "+33612345642" -> "06 •• •• •• 42"
export const maskPhone = (phone?: string): string => {
  if (!phone) return ""
  const national = phone.replace(/\s/g, "").replace(/^\+33/, "0")
  if (/^0\d{9}$/.test(national)) {
    return `${national.slice(0, 2)} •• •• •• ${national.slice(-2)}`
  }
  return `${national.slice(0, 3)} •• •• ${national.slice(-2)}`
}

const errorText = (error: unknown, fallback: string): string =>
  typeof error === "string" && error ? error : fallback

const RowButton = ({ picto, text, onClick }: { picto: string; text: string; onClick: () => void }) => (
  <div className={styles.Row} onClick={onClick}>
    <span className={styles.Picto} style={cdnBackground(picto)} />
    <span className={styles.RowText}>{text}</span>
    <CaretRightOutlined className={styles.Caret} />
  </div>
)

export const AccountPicto = () => <span className={styles.Picto} style={cdnBackground("/icons/account_user_picto.svg")} />

const CodeStep = ({
  phone,
  transport,
  hasValidEmail,
  loading,
  message,
  onSubmit,
  onResend,
}: {
  phone: string
  transport: Transport
  hasValidEmail: boolean
  loading: boolean
  message?: MessageInterface
  onSubmit: (code: string) => void
  onResend: (transport: Transport) => void
}) => {
  const { languages } = useLanguage()
  const initialTime: number = config.get("timeout.TwoFa_CodeResend_timer")
  const [code, setCode] = useState("")
  const [timer, setTimer] = useState(initialTime)
  const [showResendModal, setShowResendModal] = useState(false)
  const [incompleteMessage, setIncompleteMessage] = useState<MessageInterface>()

  useEffect(() => {
    if (timer <= 0) return
    const timeout = setTimeout(() => setTimer(timer - 1), 1000)
    return () => clearTimeout(timeout)
  }, [timer])

  useEffect(() => {
    if (message?.type === "error") setCode("")
  }, [message])

  return (
    <>
      <div className={styles.Context}>{languages.accountDeletionContext}</div>
      <div className={styles.StepTitle}>{languages.accountDeletionCodeTitle(transport)}</div>
      <div
        className={styles.Text}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(languages.accountDeletionCodeSentTo(transport, phone)),
        }}
      />
      <Message message={message} />
      <Message message={incompleteMessage} />
      <form
        className={styles.CodeForm}
        onSubmit={(e) => {
          e.preventDefault()
          if (code.length === CODE_LENGTH) onSubmit(code)
          else setIncompleteMessage({ type: "error", text: languages.twoFa_enterValideCode })
        }}
      >
        <Keyboard
          options={{ type: "twoFa", inputName: "twoFa" }}
          value={code}
          onChange={setCode}
          containerStyle={{ minHeight: 0, gap: 40 }}
        >
          <Code
            value={code}
            {...({ onChange: setCode, numberOfChar: CODE_LENGTH } as unknown as CodeInputProps)}
            readOnly
          />
        </Keyboard>
        <Buttons>
          <Button
            type="primary"
            danger
            wide="long"
            loading={loading}
            disabled={loading}
          >
            {languages.deleteMyAccount}
          </Button>
        </Buttons>
      </form>
      <Buttons style={{ paddingTop: 10 }}>
        {timer > 0 ? (
          <div className={styles.ResendInTime}>
            {`${languages.sendBackTheCode} ${timer} ${timer <= 1 ? "SECONDE" : "SECONDES"}`}
          </div>
        ) : (
          <Button type="link" size="small" onClick={() => setShowResendModal(true)}>
            {languages.twoFa_resendCode}
          </Button>
        )}
      </Buttons>
      <TwoFaModal
        showModal={showResendModal}
        hasValidEmail={hasValidEmail}
        handleCloseModal={(e) => {
          e.preventDefault()
          setShowResendModal(false)
        }}
        handleResendBy={(value: Transport) => {
          setShowResendModal(false)
          setTimer(initialTime)
          onResend(value)
        }}
      />
    </>
  )
}

export const AccountDeletionModal = ({ visible, onClose }: { visible: boolean; onClose: () => void }) => {
  const { languages } = useLanguage()
  const dispatch = useDispatch()
  const customer = useSelector(({ client }: { client: ClientStore }) => client.customer)
  const [step, setStep] = useState<Step>("menu")
  const [verifyId, setVerifyId] = useState<string>()
  const [transport, setTransport] = useState<Transport>("sms")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState<MessageInterface>()

  const hasValidEmail = Boolean(customer?.email) && customer?.email !== config.get("mock.noemail")

  const goTo = (next: Step) => {
    setMessage(undefined)
    setStep(next)
  }

  const close = () => {
    setVerifyId(undefined)
    goTo("menu")
    onClose()
  }

  const requestDeletion = (values: { password?: string }) => {
    const route = config.get("patient.post.account_deletion")
    setLoading(true)
    setMessage(undefined)
    request<DeletionRequestResponse>(route, {
      method: "POST",
      payload: { old_password: values.password },
    })
      .then((res) => {
        if (res?.status === "ok" && res.confirmed) {
          goTo("done")
        } else if (res?.status === "ok" && res.customer?.update_verify_id) {
          setVerifyId(res.customer.update_verify_id)
          setTransport("sms")
          goTo("code")
        } else {
          setMessage({ type: "error", text: languages.SomethingWentWrong })
        }
      })
      .catch((error) => {
        console.error(error, { route })
        setMessage({
          type: "error",
          text:
            error === languages.mismatchingErrorForm
              ? languages.accountDeletionWrongPassword
              : errorText(error, languages.SomethingWentWrong),
        })
      })
      .finally(() => setLoading(false))
  }

  const verifyCode = (token: string) => {
    const route = config.get("patient.post.account_deletion_verify")
    setLoading(true)
    setMessage(undefined)
    request<{ status: string }>(route, {
      method: "POST",
      payload: { token, update_verify_id: verifyId },
    })
      .then((res) => {
        if (res?.status === "ok") goTo("done")
        else setMessage({ type: "error", text: languages.twoFa_invalidCode })
      })
      .catch((error) => {
        console.error(error, { route })
        setMessage({
          type: "error",
          text:
            error === languages.nexmoError
              ? languages.twoFa_invalidCode
              : errorText(error, languages.twoFa_invalidCode),
        })
      })
      .finally(() => setLoading(false))
  }

  const resendCode = (value: Transport) => {
    const route = `${config.get("patient.post.account_deletion_token")}/${value}`
    setMessage(undefined)
    request<DeletionResendResponse>(route, {
      method: "POST",
      payload: { update_verify_id: verifyId },
    })
      .then((res) => {
        if (res?.status === "ok" && res.update_verify_id) {
          setVerifyId(res.update_verify_id)
          setTransport(res.type || value)
        } else {
          setMessage({ type: "error", text: languages.CouldntSendTheCode })
        }
      })
      .catch((error) => {
        console.error(error, { route })
        setMessage({ type: "error", text: errorText(error, languages.CouldntSendTheCode) })
      })
  }

  const header = (onBack?: () => void) => (
    <div className={`${styles.Header} ${onBack ? styles.HeaderWithBack : ""}`}>
      {onBack ? (
        <PreviousButton
          onClick={onBack}
          style={{ top: 40, left: 45, height: 60, padding: "0 20px", display: "flex", alignItems: "center" }}
        />
      ) : (
        <span />
      )}
      <div
        className={styles.Close}
        style={cdnBackground("/icons/close_red.svg")}
        role="button"
        aria-label={languages.close}
        onClick={close}
      />
    </div>
  )

  const renderStep = () => {
    switch (step) {
      case "menu":
        return (
          <>
            {header()}
            <div className={styles.ModalTitle}>{languages.myAccount}</div>
            <div className={styles.MenuBody}>
              <RowButton
                picto="/icons/account_delete_picto.svg"
                text={languages.deleteMyAccount}
                onClick={() => goTo("warning")}
              />
            </div>
          </>
        )
      case "warning":
        return (
          <>
            {header()}
            <div className={styles.Centered}>
              <span className={styles.StatusPicto} style={cdnBackground("/icons/account_deletion_warning.svg")} />
              <div className={styles.Title}>{languages.accountDeletionWarningTitle}</div>
              <div
                className={styles.Text}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(languages.accountDeletionWarningText_html),
                }}
              />
              <Buttons style={{ paddingTop: 0 }}>
                <Button onClick={close}>{languages.cancel}</Button>
                <Button type="primary" danger onClick={() => goTo("password")}>
                  {languages.deleteMyAccount}
                </Button>
              </Buttons>
            </div>
          </>
        )
      case "password":
        return (
          <>
            {header(() => goTo("warning"))}
            <div className={styles.Context}>{languages.accountDeletionContext}</div>
            <Message message={message} />
            <FormPage
              title={languages.accountDeletionEnterPassword}
              inputs={[{ name: "password", required: true }]}
              acceptText={languages.continue}
              loading={loading}
              onFinish={requestDeletion}
              onCancel={() => goTo("warning")}
              hidePrevious
            />
            <Buttons style={{ paddingTop: 10 }}>
              <Button
                type="link"
                size="small"
                className={styles.ForgotLink}
                onClick={() => dispatch(logout(RESET_PASSWORD_PAGE))}
              >
                {languages.forgottenPassword}
              </Button>
            </Buttons>
          </>
        )
      case "code":
        return (
          <>
            {header(() => goTo("password"))}
            <CodeStep
              phone={maskPhone(customer?.phone)}
              transport={transport}
              hasValidEmail={hasValidEmail}
              loading={loading}
              message={message}
              onSubmit={verifyCode}
              onResend={resendCode}
            />
          </>
        )
      case "done":
        return (
          <div className={styles.Centered}>
            <span className={styles.StatusPicto} style={cdnBackground("/icons/account_deletion_done.svg")} />
            <div className={styles.Title}>{languages.accountDeletionDoneTitle}</div>
            <div
              className={styles.Text}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(languages.accountDeletionDoneText_html),
              }}
            />
            <Buttons style={{ paddingTop: 0 }}>
              <Button type="primary" wide="long" onClick={() => dispatch(logout())}>
                {languages.finishAndLogout}
              </Button>
            </Buttons>
          </div>
        )
    }
  }

  return (
    <GenericModal visible={visible} noFooter style={{ width: 1476 }}>
      <div className={styles.Body}>{renderStep()}</div>
    </GenericModal>
  )
}
